import {KeyboardEvent as ReactKeyboardEvent, MutableRefObject, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import type {HorizontalTimelineItem} from "./HorizontalTimelineContainer";

// Generous upper bound for a native smooth scroll to settle; the active index is pinned to the
// destination until then so a multi-card jump doesn't ripple through intermediate cards.
const SCROLL_SETTLE_MS = 480

function readPad(el: HTMLElement): number {
  const v = parseInt(getComputedStyle(el).scrollPaddingLeft, 10)
  return Number.isFinite(v) ? v : 0
}

interface MouseDragOptions {
  // if set, scroll this element instead of the drag element (for forwarding from axis to strip)
  scrollTarget?: HTMLElement
  // called on pointerdown, e.g. to cancel an in-flight programmatic scroll
  onGrab?: () => void
  // called on release; the caller decides whether/how to snap
  onSettle: (moved: boolean) => void
}

// Mouse-only 1:1 click-drag scrolling for the card strip. On touch the browser scrolls the
// strip natively (overflow-x: auto + touch-action: pan-x) with momentum and scroll-snap, so
// engaging a JS drag there would fight native scrolling — hence non-mouse pointers are ignored.
function attachMouseDragScroll(el: HTMLElement, opts: MouseDragOptions): () => void {
  let down = false, startX = 0, startScroll = 0, moved = false
  // set on release after a real drag; consumed by the capturing click handler so a drag that
  // ends over a link/button inside a card doesn't also fire that element's click.
  let suppressClick = false
  const scrollEl = opts.scrollTarget ?? el
  const onMove = (e: PointerEvent) => {
    if (!down) return
    const dx = e.clientX - startX
    if (Math.abs(dx) > 3) moved = true
    const max = scrollEl.scrollWidth - scrollEl.clientWidth
    scrollEl.scrollLeft = Math.max(0, Math.min(max, startScroll - dx))
  }
  const onUp = () => {
    if (!down) return
    down = false
    suppressClick = moved
    el.style.cursor = "grab"
    document.removeEventListener("pointermove", onMove)
    document.removeEventListener("pointerup", onUp)
    document.removeEventListener("pointercancel", onUp)
    opts.onSettle(moved)
  }
  // Capturing so it runs before the card's own link/button handlers; consumes at most the single
  // click the browser synthesizes right after a drag release.
  const onClick = (e: MouseEvent) => {
    if (!suppressClick) return
    suppressClick = false
    e.preventDefault()
    e.stopPropagation()
  }
  const onDown = (e: PointerEvent) => {
    // a fresh press clears any stale suppression from a drag that released without a click
    suppressClick = false
    if (e.pointerType !== "mouse") return
    down = true; moved = false
    startX = e.clientX; startScroll = scrollEl.scrollLeft
    scrollEl.style.scrollSnapType = "none"
    el.style.cursor = "grabbing"
    opts.onGrab?.()
    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
    document.addEventListener("pointercancel", onUp)
  }
  el.addEventListener("pointerdown", onDown)
  el.addEventListener("click", onClick, true)
  return () => {
    el.removeEventListener("pointerdown", onDown)
    el.removeEventListener("click", onClick, true)
    if (down) {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
      document.removeEventListener("pointercancel", onUp)
    }
  }
}

export interface HorizontalTimelineControls {
  active: number
  atEnd: boolean
  goTo: (index: number, smooth: boolean) => void
  goToStart: () => void
  goToEnd: () => void
  step: (dir: number) => void
  onStripKeyDown: (e: ReactKeyboardEvent) => void
}

// Owns the imperative carousel/axis mechanics for HorizontalTimelineContainer: scroll-position
// tracking, the synced time-axis transform, pointer-drag + wheel scrubbing, keyboard nav,
// resize re-sync, and view-preservation across data updates. The component only renders markup
// and reads back the discrete `active` index plus the bound callbacks.
export function useHorizontalTimeline(
  carouselRef: RefObject<HTMLDivElement>,
  trackRef: RefObject<HTMLDivElement>,
  axisRef: RefObject<HTMLElement>,
  items: HorizontalTimelineItem[],
  // true when the reader is in a windowed (deep-linked / scrolled-back) view. Routes a newer
  // prepend: windowed → "load more newer" (advance one toward live). Read as the pre-update value
  // (see prevWindowedRef).
  isWindowed: boolean,
  // true when refresh is `auto`: a live (non-windowed) prepend arrived in the background and must
  // hold the reader's position; when false (`notify`) it is a solicited apply → jump to newest.
  autoRefresh: boolean,
  // set by the container the moment "back to live" is clicked; the resulting item change routes to
  // index 0 (newest) rather than being mistaken for a "load more newer" prepend, which lands one
  // step short when the refreshed live set overlaps the old window.
  backToLivePendingRef?: MutableRefObject<boolean>,
): HorizontalTimelineControls {
  // true while a programmatic smooth scroll is gliding to its target, so the scroll listener
  // leaves `active` pinned to the destination instead of walking through intermediate cards.
  const programmaticRef = useRef(false)
  const settleTimerRef = useRef<number | undefined>(undefined)
  // anchors the on-screen position of the active card across data updates, so prepended
  // (newer) posts never shift the reader's view without their action.
  const anchorRef = useRef<{id: string, offset: number} | undefined>(undefined)
  // the windowed state as of the previous committed item set, so a prepend is attributed to the
  // action that caused it (windowed → "load more newer"; live → applied notification).
  const prevWindowedRef = useRef(isWindowed)
  const idsSignatureRef = useRef<string>("")
  // cached card NodeList so the hot scroll path doesn't re-query the DOM every frame.
  const cardsCacheRef = useRef<HTMLElement[] | null>(null)
  const [active, setActive] = useState(0)
  const [atEnd, setAtEnd] = useState(false)

  const cards = useCallback(() => {
    if (cardsCacheRef.current) return cardsCacheRef.current
    const el = carouselRef.current
    const list = el ? Array.from(el.querySelectorAll<HTMLElement>("[data-card]")) : []
    cardsCacheRef.current = list
    return list
  }, [carouselRef])

  // Invalidate the card cache whenever the item set changes; declared before the anchor effect
  // below so the cache is fresh when that effect reads it. Keyed on `items` (not items.length)
  // because prepend/append/removal all change DOM order even when the count is stable.
  useLayoutEffect(() => {
    cardsCacheRef.current = null
  }, [items])

  const cancelProgrammatic = useCallback(() => {
    programmaticRef.current = false
    if (settleTimerRef.current) {
      window.clearTimeout(settleTimerRef.current)
      settleTimerRef.current = undefined
    }
  }, [])

  const nearestIndex = useCallback(() => {
    const el = carouselRef.current
    if (!el) return 0
    const list = cards()
    if (!list.length) return 0
    const max = el.scrollWidth - el.clientWidth
    if (el.scrollLeft >= max - 2) return list.length - 1
    const ref = el.scrollLeft + readPad(el)
    let best = 0, bestD = Infinity
    list.forEach((c, i) => {
      const d = Math.abs(c.offsetLeft - ref)
      if (d < bestD) { bestD = d; best = i }
    })
    return best
  }, [cards, carouselRef])

  // Mirror the carousel: shift the axis so the active tick rests flush over its card, clamped at the ends.
  const updateTrackSync = useCallback(() => {
    const track = trackRef.current
    const el = carouselRef.current
    if (!track || !el) return
    const list = cards()
    const ticks = Array.from(track.querySelectorAll<HTMLElement>("[data-tick]"))
    if (!list.length || !ticks.length) return

    const pad = readPad(el)
    const maxC = (el.scrollWidth - el.clientWidth) || 1
    // visible track width = the axis content box, i.e. the axis's own horizontal padding excluded.
    // Derive that padding from the track's offsetLeft (= the axis left padding; it is symmetric)
    // rather than the strip's scroll-padding — they differ in the non-cards variant (the strip has
    // no scroll-padding → 0), which left maxT too small and clipped the last tick at the right end.
    const parent = track.parentElement
    const avail = (parent?.clientWidth ?? track.clientWidth) - 2 * track.offsetLeft
    // Include all track children (not just [data-tick]) so trailing dots count toward maxT.
    let contentRight = 0
    Array.from(track.children).forEach(r => {
      const child = r as HTMLElement
      contentRight = Math.max(contentRight, child.offsetLeft + child.offsetWidth)
    })
    const maxT = Math.max(0, contentRight - avail)

    const cardScroll = list.map(c => Math.min(Math.max(0, c.offsetLeft - pad), maxC))
    const s = el.scrollLeft
    let i = 0
    while (i < cardScroll.length - 1 && s >= cardScroll[i + 1]) i++
    const j = Math.min(i + 1, cardScroll.length - 1)
    const span = (cardScroll[j] - cardScroll[i]) || 1
    const frac = Math.max(0, Math.min(1, (s - cardScroll[i]) / span))
    const f = i + (j - i) * frac

    // leadingOffset: how far the first event tick is from the track start (non-zero when leading
    // dots are present). Subtracting it from dotPos keeps shift=0 at s=0, so leading dots stay
    // in the visible area instead of being pushed off-screen to the left.
    const leadingOffset = ticks.length > 0 ? Math.max(0, ticks[0].offsetLeft - track.offsetLeft) : 0
    const lo = Math.floor(f), hi = Math.min(lo + 1, ticks.length - 1)
    const dotPos = (ticks[lo].offsetLeft - leadingOffset) + (ticks[hi].offsetLeft - ticks[lo].offsetLeft) * (f - lo)
    const shift = Math.min(0, Math.max(-maxT, -dotPos))
    track.style.transition = "none"
    track.style.transform = `translateX(${shift}px)`
  }, [cards, carouselRef, trackRef])

  const goTo = useCallback((index: number, smooth: boolean) => {
    const el = carouselRef.current
    const list = cards()
    const card = list[index]
    if (!el || !card) return
    const max = el.scrollWidth - el.clientWidth
    const target = Math.max(0, Math.min(max, card.offsetLeft - readPad(el)))
    setActive(index)
    if (smooth) {
      // Disable snap during the glide so the browser doesn't immediately re-snap to a different
      // position before the smooth scroll lands. Re-enable once the scroll settles at the target.
      el.style.scrollSnapType = "none"
      programmaticRef.current = true
      if (settleTimerRef.current) window.clearTimeout(settleTimerRef.current)
      settleTimerRef.current = window.setTimeout(() => {
        programmaticRef.current = false
        settleTimerRef.current = undefined
        el.style.scrollSnapType = "x mandatory"
        updateTrackSync()
      }, SCROLL_SETTLE_MS)
      el.scrollTo({left: target, behavior: "smooth"})
    } else {
      el.scrollLeft = target
      updateTrackSync()
    }
  }, [cards, updateTrackSync, carouselRef])

  // Scroll to the strip's absolute end, snapping the load-more button into view. Same
  // smooth-scroll mechanics as goTo, but targets max rather than a card snap position.
  const goToEnd = useCallback(() => {
    const el = carouselRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    el.style.scrollSnapType = "none"
    programmaticRef.current = true
    if (settleTimerRef.current) window.clearTimeout(settleTimerRef.current)
    settleTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false
      settleTimerRef.current = undefined
      el.style.scrollSnapType = "x mandatory"
      updateTrackSync()
    }, SCROLL_SETTLE_MS)
    el.scrollTo({left: max, behavior: "smooth"})
  }, [carouselRef, updateTrackSync])

  // Scroll to the strip's absolute start, bringing the lead control (back-to-live / load-more-newer)
  // fully into view. Mirror of goToEnd: targets 0 rather than a card snap position, so the lead
  // button that sits before the first card is revealed rather than left clipped off the left edge.
  const goToStart = useCallback(() => {
    const el = carouselRef.current
    if (!el) return
    el.style.scrollSnapType = "none"
    programmaticRef.current = true
    if (settleTimerRef.current) window.clearTimeout(settleTimerRef.current)
    settleTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false
      settleTimerRef.current = undefined
      el.style.scrollSnapType = "x mandatory"
      updateTrackSync()
    }, SCROLL_SETTLE_MS)
    el.scrollTo({left: 0, behavior: "smooth"})
  }, [carouselRef, updateTrackSync])

  const step = useCallback((dir: number) => {
    const el = carouselRef.current
    const list = cards()
    const nearest = nearestIndex()
    if (dir > 0 && el) {
      const max = el.scrollWidth - el.clientWidth
      if (el.scrollLeft >= max - 2) return  // already at strip end, nothing to do
      if (nearest >= list.length - 1) {
        goToEnd()  // at last card — one more step forward reveals the load-more button
        return
      }
    }
    const next = Math.max(0, Math.min(list.length - 1, nearest + dir))
    goTo(next, true)
  }, [cards, goTo, goToEnd, nearestIndex, carouselRef])

  const onStripKeyDown = useCallback((e: ReactKeyboardEvent) => {
    if (e.key === "ArrowRight") { e.preventDefault(); step(1) }
    else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1) }
  }, [step])

  // carousel: scroll-sync (axis transform + active index) + pointer drag
  useEffect(() => {
    const el = carouselRef.current
    if (!el) return
    const axisEl = axisRef.current
    let throttled = false
    const onScroll = () => {
      updateTrackSync()
      const max = el.scrollWidth - el.clientWidth
      setAtEnd(el.scrollLeft >= max - 2)
      const idx = nearestIndex()
      const c = cards()[idx]
      if (c) anchorRef.current = {id: c.dataset.id ?? "", offset: c.offsetLeft - el.scrollLeft}
      if (programmaticRef.current || throttled) return
      throttled = true
      window.setTimeout(() => {
        throttled = false
        setActive(prev => (idx !== prev ? idx : prev))
      }, 60)
    }
    el.addEventListener("scroll", onScroll, {passive: true})
    const detachDrag = attachMouseDragScroll(el, {
      onGrab: cancelProgrammatic,
      onSettle: (moved) => {
        if (moved) {
          const max = el.scrollWidth - el.clientWidth
          const nearest = nearestIndex()
          const list = cards()
          // If drag ended in the zone between the last card's snap and the strip end, snap to end
          // (same behaviour as native touch scroll with scroll-snap-align:end on .tik4-htl__more).
          if (nearest >= list.length - 1 && list.length > 0) {
            const lastCard = list[list.length - 1]
            const lastSnap = Math.max(0, Math.min(max, lastCard.offsetLeft - readPad(el)))
            if (el.scrollLeft > (lastSnap + max) / 2) {
              goToEnd()
              return
            }
          }
          goTo(nearest, true)
        } else {
          el.style.scrollSnapType = "x mandatory"
        }
      },
    })

    // Axis forwarding: mouse-drag, wheel, and touch all scroll the card strip.
    // scroll-snap-type must be disabled before any direct scrollLeft assignment —
    // otherwise mandatory snap fights every programmatic change and nothing moves visibly.
    let wheelTimer: number | undefined
    const onAxisWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return
      e.preventDefault()
      if (wheelTimer === undefined) {
        cancelProgrammatic()
        el.style.scrollSnapType = "none"
      }
      window.clearTimeout(wheelTimer)
      el.scrollLeft += e.deltaX
      wheelTimer = window.setTimeout(() => {
        wheelTimer = undefined
        goTo(nearestIndex(), true)
      }, 150)
    }
    // Touch forwarding via pointer events (touch type only). Pointer events fire reliably
    // regardless of touch-action, unlike touchmove which browsers can suppress mid-gesture.
    // Mouse is handled by attachMouseDragScroll above; each handler guards on pointerType.
    let axisTouchStartX = 0, axisTouchStartY = 0, axisTouchStartScroll = 0
    let axisTouchMoved = false, axisTouchLocked: 'h' | 'v' | null = null
    const onAxisTouchDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return
      axisTouchStartX = e.clientX; axisTouchStartY = e.clientY
      axisTouchStartScroll = el.scrollLeft
      axisTouchMoved = false; axisTouchLocked = null
      axisEl?.setPointerCapture(e.pointerId)
    }
    const onAxisTouchMove = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return
      const dx = e.clientX - axisTouchStartX
      const dy = e.clientY - axisTouchStartY
      if (!axisTouchLocked) {
        if (Math.abs(dx) > Math.abs(dy) + 5) {
          axisTouchLocked = 'h'
          cancelProgrammatic()
          el.style.scrollSnapType = "none"
        } else if (Math.abs(dy) > Math.abs(dx) + 5) {
          axisTouchLocked = 'v'
        } else return
      }
      if (axisTouchLocked === 'h') {
        axisTouchMoved = true
        el.scrollLeft = axisTouchStartScroll - dx
      }
    }
    const onAxisTouchUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return
      if (axisTouchMoved) goTo(nearestIndex(), true)
      else if (axisTouchLocked === 'h') el.style.scrollSnapType = "x mandatory"
    }
    let detachAxisDrag: (() => void) | undefined
    if (axisEl) {
      detachAxisDrag = attachMouseDragScroll(axisEl, {
        scrollTarget: el,
        onGrab: cancelProgrammatic,
        onSettle: (moved) => {
          if (moved) goTo(nearestIndex(), true)
          else el.style.scrollSnapType = "x mandatory"
        },
      })
      axisEl.addEventListener("wheel", onAxisWheel, {passive: false})
      axisEl.addEventListener("pointerdown", onAxisTouchDown)
      axisEl.addEventListener("pointermove", onAxisTouchMove)
      axisEl.addEventListener("pointerup", onAxisTouchUp)
      axisEl.addEventListener("pointercancel", onAxisTouchUp)
    }

    updateTrackSync()
    return () => {
      el.removeEventListener("scroll", onScroll)
      detachDrag()
      detachAxisDrag?.()
      if (wheelTimer !== undefined) window.clearTimeout(wheelTimer)
      if (axisEl) {
        axisEl.removeEventListener("wheel", onAxisWheel)
        axisEl.removeEventListener("pointerdown", onAxisTouchDown)
        axisEl.removeEventListener("pointermove", onAxisTouchMove)
        axisEl.removeEventListener("pointerup", onAxisTouchUp)
        axisEl.removeEventListener("pointercancel", onAxisTouchUp)
      }
    }
  }, [cards, goTo, goToEnd, nearestIndex, updateTrackSync, cancelProgrammatic, carouselRef, axisRef])

  // re-sync layout-derived geometry (axis transform, snap alignment) after a viewport resize
  // or device rotation, which the scroll-only path would otherwise leave stale.
  useEffect(() => {
    const el = carouselRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    let raf: number | undefined
    let first = true
    const ro = new ResizeObserver(() => {
      if (first) { first = false; return }
      if (raf) window.cancelAnimationFrame(raf)
      raf = window.requestAnimationFrame(() => goTo(nearestIndex(), false))
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [goTo, nearestIndex, carouselRef])

  // Move focus to loaded content when the event set changes. The strip is navigated by discrete
  // controls, so an explicit, solicited load reveals what it loaded rather than preserving the
  // prior position (see design "focus follows explicit load/navigation actions"). Runs after the
  // cache-invalidation effect above.
  useLayoutEffect(() => {
    const el = carouselRef.current
    if (!el) return
    const newIds = items.map(i => i.event.local_id ?? "")
    const sig = newIds.join(",")
    const prevSig = idsSignatureRef.current
    idsSignatureRef.current = sig
    if (!prevSig || prevSig === sig) return
    const max = () => el.scrollWidth - el.clientWidth

    // "Back to live" always resolves to the newest post regardless of how the live set overlaps the
    // old window — take this before the prepend heuristics so an overlapping refresh isn't mistaken
    // for "load more newer" (which would land on inserted - 1).
    if (backToLivePendingRef?.current) {
      backToLivePendingRef.current = false
      goTo(0, true)
      return
    }

    const oldIds = prevSig.split(",")
    const inserted = newIds.length - oldIds.length
    if (inserted > 0) {
      // Older "load more": appended at the right → advance to the first newly-loaded older post.
      if (oldIds.every((id, k) => id === newIds[k])) {
        goTo(oldIds.length, true)
        return
      }
      // Newer posts prepended at the left:
      //  - windowed → "load more newer" → advance one card toward live;
      //  - live + notify → an applied "new posts" notification → jump to the newest;
      //  - live + auto → a background update → hold the reader's position (fall through to the
      //    anchor branch below) so the prepend doesn't yank them.
      if (oldIds.every((id, k) => id === newIds[newIds.length - oldIds.length + k])) {
        if (prevWindowedRef.current) { goTo(inserted - 1, true); return }
        if (!autoRefresh) { goTo(0, true); return }
        // auto + live background prepend: hold the reader on their current post. The scroll listener
        // normally keeps anchorRef current, but at the initial/idle live edge no scroll has fired
        // yet, leaving it unset — seed it from the active card (at rest it sits readPad from the
        // strip start) so the fall-through re-pins that post instead of jumping to the new one.
        if (!anchorRef.current?.id) {
          const heldId = oldIds[active]
          if (heldId) anchorRef.current = {id: heldId, offset: readPad(el)}
        }
      }
    }

    // Not a solicited load (replacement, deletion, reorder, deep-link): preserve the active card's
    // on-screen offset if it survives, else land on the newest. For a deep-link jump the reset
    // fires here, then scrollToElement scrolls on to the target.
    const anchor = anchorRef.current
    if (anchor?.id) {
      const c = el.querySelector<HTMLElement>(`[data-card][data-id="${anchor.id}"]`)
      if (c) {
        el.scrollLeft = Math.max(0, Math.min(max(), c.offsetLeft - anchor.offset))
        const i = cards().indexOf(c)
        if (i >= 0) setActive(i)
        updateTrackSync()
        return
      }
    }
    el.scrollLeft = 0
    setActive(0)
    updateTrackSync()
  }, [items, active, autoRefresh, backToLivePendingRef, cards, goTo, updateTrackSync, carouselRef])

  useEffect(() => { prevWindowedRef.current = isWindowed }, [isWindowed])

  useEffect(() => { updateTrackSync() }, [items.length, updateTrackSync])

  useEffect(() => () => {
    if (settleTimerRef.current) window.clearTimeout(settleTimerRef.current)
  }, [])

  return {active, atEnd, goTo, goToStart, goToEnd, step, onStripKeyDown}
}
